import React, { useState } from "react";
import { getGradientClass } from "../constants/theme";
import { raceYear } from "../data/races";

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function RaceCalendar({ races }) {
    // Initialize to first race month, or current month if no races
    const getInitialMonth = () => {
        const raceMonths = races
            .filter(race => race.dateObj)
            .map(race => race.dateObj.getMonth())
            .sort((a, b) => a - b);
        return raceMonths.length > 0 ? raceMonths[0] : new Date().getMonth();
    };

    const [currentMonth, setCurrentMonth] = useState(getInitialMonth());
    const [currentYear, setCurrentYear] = useState(raceYear);
    const [selectedRace, setSelectedRace] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);

    // Get first day of month and number of days
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    // Group races by date - use consistent dateKey format
    const racesByDate = {};
    races.forEach(race => {
        if (race.dateObj) {
            const year = race.dateObj.getFullYear();
            const month = race.dateObj.getMonth();
            const day = race.dateObj.getDate();
            const dateKey = `${year}-${month}-${day}`;
            if (!racesByDate[dateKey]) {
                racesByDate[dateKey] = [];
            }
            racesByDate[dateKey].push(race);
        }
    });

    // Generate calendar days
    const calendarDays = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < firstDayOfMonth; i++) {
        calendarDays.push(null);
    }
    
    // Add days of the month - use same dateKey format
    for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(currentYear, currentMonth, day);
        const dateKey = `${currentYear}-${currentMonth}-${day}`;
        const dayRaces = racesByDate[dateKey] || [];
        calendarDays.push({ day, date, races: dayRaces });
    }

    const navigateMonth = (direction) => {
        if (direction === 'prev') {
            if (currentMonth === 0) {
                setCurrentMonth(11);
                setCurrentYear(currentYear - 1);
            } else {
                setCurrentMonth(currentMonth - 1);
            }
        } else {
            if (currentMonth === 11) {
                setCurrentMonth(0);
                setCurrentYear(currentYear + 1);
            } else {
                setCurrentMonth(currentMonth + 1);
            }
        }
    };

    const isToday = (date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
               date.getMonth() === today.getMonth() &&
               date.getFullYear() === today.getFullYear();
    };

    // Get upcoming races for preview
    const getUpcomingRacesPreview = () => {
        const now = new Date();
        return races
            .filter(race => race.dateObj && race.dateObj >= now)
            .sort((a, b) => a.dateObj - b.dateObj)
            .slice(0, 3);
    };

    const upcomingPreview = getUpcomingRacesPreview();

    return (
        <div className="relative">
            {/* Collapsed Preview */}
            {!isExpanded && (
                <div 
                    className="bg-gray-800/50 rounded-2xl p-6 backdrop-blur-sm border border-sky-400/20 cursor-pointer hover:border-sky-400/50 transition-all duration-200"
                    onClick={() => setIsExpanded(true)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-yellow-400 to-sky-400 mb-3">
                                 {currentYear} Calendar
                            </h3>
                            {upcomingPreview.length > 0 && (
                                <div className="space-y-2">
                                    <p className="text-gray-400 text-sm mb-2">Upcoming races:</p>
                                    {upcomingPreview.map((race, idx) => (
                                        <div key={race.id} className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${getGradientClass(idx)}`}></div>
                                            <span className="text-gray-300 text-sm">
                                                {race.dateObj?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {race.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="ml-4">
                            <div className="px-6 py-3 rounded-lg bg-gradient-to-r from-sky-400 to-yellow-400 text-white font-bold text-sm hover:from-sky-500 hover:to-yellow-500 transition-all duration-200 transform hover:scale-105 shadow-lg">
                                View Calendar ↓
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Expanded Calendar */}
            {isExpanded && (
                <div className="space-y-4">
                    {/* Toggle Button */}
                    <div className="flex justify-end">
                        <button
                            onClick={() => setIsExpanded(false)}
                            className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-semibold text-sm transition-all duration-200 flex items-center gap-2"
                        >
                            <span>↑</span> Collapse Calendar
                        </button>
                    </div>

                    {/* Month Navigation */}
                    <div className="flex items-center justify-between mb-8">
                        <button
                            onClick={() => navigateMonth('prev')}
                            className="px-4 py-2 rounded-lg bg-gradient-to-r from-sky-400 to-blue-500 hover:from-sky-500 hover:to-blue-600 text-white font-bold transition-all duration-200 transform hover:scale-105 shadow-lg"
                        >
                            ← Prev
                        </button>
                        <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-yellow-400 to-sky-400">
                            {MONTHS[currentMonth]} {currentYear}
                        </h2>
                        <button
                            onClick={() => navigateMonth('next')}
                            className="px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white font-bold transition-all duration-200 transform hover:scale-105 shadow-lg"
                        >
                            Next →
                        </button>
                    </div>

                    {/* Calendar Grid */}
                    <div className="bg-gray-800/50 rounded-2xl p-6 backdrop-blur-sm border border-sky-400/20">
                        {/* Day Headers */}
                        <div className="grid grid-cols-7 gap-2 mb-4">
                            {DAYS.map(day => (
                                <div
                                    key={day}
                                    className="text-center font-bold text-sky-400 py-2 text-sm md:text-base"
                                >
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Calendar Days */}
                        <div className="grid grid-cols-7 gap-2">
                            {calendarDays.map((dayData, index) => {
                                if (!dayData) {
                                    return <div key={`empty-${index}`} className="aspect-square" />;
                                }

                                const { day, date, races } = dayData;
                                const today = isToday(date);
                                const hasRaces = races.length > 0;

                                return (
                                    <div
                                        key={day}
                                        className={`aspect-square relative rounded-lg p-2 transition-all duration-200 ${
                                            today
                                                ? "ring-2 ring-yellow-400 ring-offset-2 ring-offset-gray-900"
                                                : ""
                                        } ${
                                            hasRaces
                                                ? "bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-sky-400/50 hover:border-sky-400 cursor-pointer hover:scale-105"
                                                : "bg-gray-800/30 border border-gray-700/50"
                                        }`}
                                        onClick={() => hasRaces && setSelectedRace(races[0])}
                                    >
                                        <div className={`text-sm font-bold mb-1 ${
                                            today ? "text-yellow-400" : hasRaces ? "text-sky-400" : "text-gray-400"
                                        }`}>
                                            {day}
                                        </div>
                                        
                                        {hasRaces && (
                                            <div className="space-y-1">
                                                {races.slice(0, 2).map((race, raceIndex) => (
                                                    <div
                                                        key={race.id}
                                                        className={`text-xs px-1.5 py-0.5 rounded bg-gradient-to-r ${getGradientClass(raceIndex)} text-white font-semibold truncate shadow-md`}
                                                        title={race.name}
                                                    >
                                                        {race.name.length > 12 ? race.name.substring(0, 12) + "..." : race.name}
                                                    </div>
                                                ))}
                                                {races.length > 2 && (
                                                    <div className="text-xs text-sky-400 font-bold">
                                                        +{races.length - 2} more
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Race Detail Modal */}
            {selectedRace && (
                <div
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedRace(null)}
                >
                    <div
                        className="bg-gray-900 rounded-2xl p-8 max-w-md w-full border-2 border-sky-400/50 shadow-2xl transform transition-all"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-yellow-400">
                                {selectedRace.name}
                            </h3>
                            <button
                                onClick={() => setSelectedRace(null)}
                                className="text-gray-400 hover:text-white text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        <div className="space-y-3 text-gray-300">
                            <div>
                                <span className="text-sky-400 font-semibold">Date: </span>
                                {selectedRace.dateObj?.toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                }) || selectedRace.date}
                            </div>
                            <div>
                                <span className="text-yellow-400 font-semibold">Location: </span>
                                {selectedRace.location}
                            </div>
                            <div>
                                <span className="text-pink-400 font-semibold">Vibe: </span>
                                {selectedRace.vibe}
                            </div>
                            {selectedRace.result && (
                                <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-yellow-400/20 to-orange-500/20 border border-yellow-400/50">
                                    <span className="text-yellow-400 font-bold">Result: </span>
                                    <span className="text-white">{selectedRace.result}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

